import { type ComputedRef, type Ref, computed } from "vue";
import { getProductTierPrices } from "@shopware/helpers";
import type { TierPrice } from "@shopware/helpers";
import type { Schemas } from "#shopware";

/** Union type for price objects - CalculatedPrice or CalculatedCheapestPrice (for parent products) */
type ProductPrice = Schemas["CalculatedPrice"] | Schemas["Product"]["calculatedCheapestPrice"]

export type UseProductPriceCustomReturn = {
    /**
     * Whole calculated price object
     */
    price: ComputedRef<ProductPrice | undefined>;
    /**
     * Calculated price value for one selling unit
     */
    totalPrice: ComputedRef<number | undefined>;
    /**
     * Current unit price value
     */
    unitPrice: ComputedRef<number | undefined>;
    /**
     * Can be used if isListPrice is set to true
     */
    referencePrice: ComputedRef<Schemas["CalculatedPrice"]["referencePrice"] | undefined>;
    /**
     * determines if `price` contains the minimum tier price
     */
    displayFrom: ComputedRef<boolean>;
    /**
     * cheapest price value for a variant if exists
     */
    displayFromVariants: ComputedRef<number | false | undefined>;
    /**
     * array of TierPrice object
     */
    tierPrices: ComputedRef<TierPrice[]>;
    /**
     * determines whether a discount price is set
     */
    isListPrice: ComputedRef<boolean>;
    /**
     * price for products with regulation price
     */
    regulationPrice: ComputedRef<number | undefined>;
    /**
     * determines if cheapest price should be displayed separately
     */
    displayCheapestPrice: ComputedRef<boolean>;
    /**
     * cheapest price unit price value
     */
    cheapestPrice: ComputedRef<number | undefined>;
};

/**
 * Enhancement of the `useProductPrice` from shopware composables to abstract the logic to expose most useful helpers for price displaying.
 *
 * Matches the original A4G hblMapProduct.ts price logic including:
 * - Parent product price override using extensions.variantListing.displayParent
 * - First tier price selection for calculatedPrices
 * - "From" display for both tier prices and parent products with variant ranges
 *
 * @public
 * @category Product
 */
export function useProductPriceCustom(
    product: Ref<Schemas["Product"] | undefined>,
): UseProductPriceCustomReturn {
    const _cheapest: ComputedRef<
        Schemas["Product"]["calculatedCheapestPrice"] | undefined
    > = computed(() => product.value?.calculatedCheapestPrice);

    /**
     * calculatedPrices are used for product with tier prices
     * Uses first element to match the original A4G mapping logic
     */
    const _realBase: ComputedRef<Schemas["CalculatedPrice"] | undefined> = computed(
        () => {
            const calculatedPrices = product.value?.calculatedPrices;
            return (calculatedPrices?.length ?? 0) > 0
                ? calculatedPrices?.[0] // Use first element (matches old A4G mapping)
                : product.value?.calculatedPrice;
        }
    );

    /**
     * Parent product should display variant price range
     * Condition: variantListing.displayParent === true AND parentId === null (is the parent)
     */
    const _displayParent: ComputedRef<boolean> = computed(() => {
        return product.value?.extensions?.variantListing?.displayParent === true
            && product.value?.parentId === null;
    });

    /**
     * For parent products with displayParent, check if variants have different prices
     */
    const _displayFromVariantsForParent: ComputedRef<boolean> = computed(() => {
        return _displayParent.value
            && _realBase.value?.unitPrice !== _cheapest.value?.unitPrice;
    });

    /**
     * The effective "real" price - for parent products with displayParent,
     * this is overridden with the cheapest variant price (matches old A4G logic)
     */
    const _real: ComputedRef<ProductPrice | undefined> = computed(
        () => {
            if (_displayParent.value && _displayFromVariantsForParent.value) {
                return _cheapest.value;
            }
            return _realBase.value;
        }
    );

    const referencePrice: ComputedRef<Schemas["CalculatedPrice"]["referencePrice"] | undefined> =
        computed(() => (_real?.value as Schemas["CalculatedPrice"] | undefined)?.referencePrice);

    /**
     * Display "from" for tier prices
     */
    const _displayFromTierPrices: ComputedRef<boolean> = computed(() => {
        return (product.value?.calculatedPrices?.length ?? 0) > 1;
    });

    /**
     * Display "from" for either tier prices OR parent products with variant price ranges
     */
    const displayFrom: ComputedRef<boolean> = computed(() => {
        return _displayFromTierPrices.value
            || (_displayParent.value && _displayFromVariantsForParent.value);
    });

    /**
     * Cheapest variant price differs from real price (for non-parent variants)
     * Old logic: _cheapest.unitPrice !== _real.unitPrice && _cheapest.variantId !== product.id
     */
    const displayFromVariants: ComputedRef<number | false | undefined> = computed(
        () => {
            return (
                _cheapest.value?.unitPrice !== _real.value?.unitPrice &&
                _cheapest.value?.variantId !== product.value?.id &&
                _cheapest.value?.unitPrice
            );
        },
    );

    const _price: ComputedRef<ProductPrice | undefined> = computed(
        () => {
            // For tier prices, find the minimum price
            if (_displayFromTierPrices.value && getProductTierPrices(product.value).length > 1) {
                return product.value?.calculatedPrices?.reduce((previous: Schemas["CalculatedPrice"], current: Schemas["CalculatedPrice"]) => {
                    return current.unitPrice < previous.unitPrice ? current : previous;
                });
            }
            // _real already handles parent product override to cheapest
            return _real.value;
        },
    );

    const unitPrice: ComputedRef<number | undefined> = computed(
        () => _price.value?.unitPrice,
    );
    const totalPrice: ComputedRef<number | undefined> = computed(
        () => _price.value?.totalPrice,
    );
    const price: ComputedRef<ProductPrice | undefined> = computed(
        () => _price.value,
    );

    const isListPrice: ComputedRef<boolean> = computed(() => {
        return !!_price.value?.listPrice?.price;
    });

    /**
     * Fixed: Use regulation price from selected price object instead of base calculatedPrice
     */
    const regulationPrice: ComputedRef<number | undefined> = computed(
        () => _price.value?.regulationPrice?.price,
    );

    const tierPrices = computed(() => getProductTierPrices(product.value));

    /**
     * Added: Logic to determine if cheapest price should be displayed separately
     * Matches Twig condition: cheapest.unitPrice != real.unitPrice and cheapest.variantId != product.id
     */
    const displayCheapestPrice: ComputedRef<boolean> = computed(() => {
        return (
            _cheapest.value?.unitPrice !== _real.value?.unitPrice &&
            _cheapest.value?.variantId !== product.value?.id
        );
    });

    /**
     * Added: Cheapest price unit price value
     */
    const cheapestPrice: ComputedRef<number | undefined> = computed(
        () => _cheapest.value?.unitPrice,
    );

    return {
        price,
        totalPrice,
        unitPrice,
        displayFromVariants,
        displayFrom,
        tierPrices,
        referencePrice,
        isListPrice,
        regulationPrice,
        displayCheapestPrice,
        cheapestPrice,
    };
}